'use client';
import CryptoJS from 'crypto-js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { secretKey } from '@app/config';
import { fetchDigitalSignatureTemplateId } from '../files/queries/use-paginated-entries';
import { DocTable } from '../files/table/doc-tables';
import { useDriveStore } from '../drive-store';
import { fetchPartyDocuments, SignatureDocument } from '../services/signature-api';
import { Loader2 } from 'lucide-react';

const decryptParams = (encryptedData: string): any => {
  const bytes = CryptoJS.AES.decrypt(
    decodeURIComponent(encryptedData),
    secretKey,
  );
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

// Check signature provider from env
const useCustomSignature = () => {
  const provider = import.meta.env.VITE_SIGNATURE_PROVIDER || 'docuseal';
  return provider === 'custom';
};

export function ConnectedWalletLayout() {
  console.log('🚀 ========================================');
  console.log('🚀 ConnectedWalletLayout MOUNTED');
  console.log('🚀 ========================================');

  const navigate = useNavigate();
  const { isConnected, address, isDisconnected } = useAccount();
  const addresss = isConnected ? address : '';
  const [walletAddress, setWalletAddress] = useState(addresss);
  const [parsedData, setParsedData] = useState<any>({});
  const [isMatchedDoc, setIsMatchedDoc] = useState<any>([]);
  const [slug, setSlug] = useState('');
  const documents = useDriveStore(s => s.documents);
  const [rowData, setRowData] = useState<any>([]);
  const [signatureUrl, setSignatureUrl] = useState<string>('');
  const [showIframe, setShowIframe] = useState(false);
  const isCustom = useCustomSignature();

  // New state for Python API integration
  const [signatureDocuments, setSignatureDocuments] = useState<SignatureDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [partyId, setPartyId] = useState<string | null>(null);

  console.log('🔍 Current State:', {
    partyId,
    isLoadingDocs,
    documentsCount: signatureDocuments.length,
    isCustom
  });

  useEffect(() => {
    if (isConnected) {
      if (address) setWalletAddress(address);
    } else setWalletAddress('');
  }, [address, isConnected, isDisconnected]);

  // ============================================
  // 🔍 FETCH PARTY_ID FROM LOCALSTORAGE
  // ============================================
  useEffect(() => {
    console.log('🔄 ========================================');
    console.log('🔄 PARTY_ID FETCH EFFECT TRIGGERED');
    console.log('🔄 ========================================');
    
    // Check localStorage immediately
    const storedPartyId = localStorage.getItem('party_id');
    const authType = localStorage.getItem('authType');
    const userEmail = localStorage.getItem('userEmail');
    
    console.log('📦 LocalStorage contents:');
    console.log('  - party_id:', storedPartyId);
    console.log('  - authType:', authType);
    console.log('  - userEmail:', userEmail);
    
    if (storedPartyId) {
      console.log('✅ ========================================');
      console.log('✅ PARTY_ID FOUND IN LOCALSTORAGE');
      console.log('✅ Value:', storedPartyId);
      console.log('✅ ========================================');
      setPartyId(storedPartyId);
      console.log('✅ setPartyId() called with:', storedPartyId);
    } else {
      console.warn('⚠️  ========================================');
      console.warn('⚠️  NO PARTY_ID IN LOCALSTORAGE');
      console.warn('⚠️  ========================================');

      if (authType === 'gmail' && userEmail) {
        console.log('🔄 Attempting to fetch party_id from API...');
        console.log('  - Email:', userEmail);
        console.log('  - API URL: http://localhost:9000/api/party-id/' + userEmail);
        
        fetch(`http://localhost:9000/api/party-id/${encodeURIComponent(userEmail)}`)
          .then(response => {
            console.log('📥 API Response Status:', response.status);
            return response.json();
          })
          .then(data => {
            console.log('📥 API Response Data:', data);
            if (data.party_id) {
              console.log('✅ Found party_id via API:', data.party_id);
              localStorage.setItem('party_id', data.party_id);
              setPartyId(data.party_id);
              console.log('✅ Saved to localStorage and state');
            } else {
              console.warn('⚠️  No party_id in API response');
            }
          })
          .catch(error => {
            console.error('❌ ========================================');
            console.error('❌ API FETCH FAILED');
            console.error('❌ Error:', error);
            console.error('❌ ========================================');
          });
      } else {
        console.warn('⚠️  Cannot fetch party_id:');
        console.warn('  - authType:', authType, '(needs to be "gmail")');
        console.warn('  - userEmail:', userEmail, '(needs to be present)');
      }
    }
  }, []);

  // ============================================
  // 🔍 FETCH DOCUMENTS FROM PYTHON API
  // ============================================
  useEffect(() => {
    console.log('🔄 ========================================');
    console.log('🔄 DOCUMENTS FETCH EFFECT TRIGGERED');
    console.log('🔄 Current partyId:', partyId);
    console.log('🔄 ========================================');

    const fetchSignatureDocs = async () => {
      if (!partyId) {
        console.log('⏳ ========================================');
        console.log('⏳ WAITING FOR PARTY_ID');
        console.log('⏳ Current value:', partyId);
        console.log('⏳ ========================================');
        return;
      }

      console.log('🚀 ========================================');
      console.log('🚀 STARTING DOCUMENT FETCH');
      console.log('🚀 Party ID:', partyId);
      console.log('🚀 ========================================');

      setIsLoadingDocs(true);
      
      try {
        console.log('📡 Calling fetchPartyDocuments...');
        console.log('  - API Base URL:', import.meta.env.VITE_SIGNATURE_DOC_API_URL || 'http://localhost:8001');
        console.log('  - Endpoint: /api/documents/party/' + partyId + '/documents');
        
        const response = await fetchPartyDocuments(partyId);
        
        console.log('📥 ========================================');
        console.log('📥 API RESPONSE RECEIVED');
        console.log('📥 Status:', response.status);
        console.log('📥 Documents Count:', response.documents?.length || 0);
        console.log('📥 Full Response:', response);
        console.log('📥 ========================================');

        if (response.status === 'success' && response.documents) {
          console.log('✅ Documents loaded successfully');
          console.log('📊 Documents:', response.documents);
          
          if (response.documents.length > 0) {
            console.log('📄 Sample document:', response.documents[0]);
          }
          
          setSignatureDocuments(response.documents);
          console.log('✅ State updated with documents');
        } else {
          console.warn('⚠️  No documents in response or unsuccessful status');
          setSignatureDocuments([]);
        }
      } catch (error) {
        console.error('❌ ========================================');
        console.error('❌ DOCUMENT FETCH FAILED');
        console.error('❌ Error:', error);
        console.error('❌ ========================================');
        setSignatureDocuments([]);
      } finally {
        setIsLoadingDocs(false);
        console.log('🏁 Document fetch completed');
      }
    };

    fetchSignatureDocs();
  }, [partyId]);

  useEffect(() => {
    if (walletAddress) {
      const urlParams = new URLSearchParams(window.location.search);
      const encryptedData = urlParams.get('token');
      if (encryptedData) {
        const decrypted = decryptParams(encryptedData);
        setParsedData(decrypted);
        if (decrypted && decrypted?.id)
          digitalSignature(decrypted?.id, decrypted);
      }
    }
  }, [walletAddress]);

  useEffect(() => {
    console.log('🔍 rowData changed:', rowData);
    console.log('🔍 walletAddress:', walletAddress);
    console.log('🔍 isCustom:', isCustom);
    console.log('🔍 Condition check:', {
      hasRowData: !!rowData,
      hasRowDataId: !!rowData?.id,
      hasDocumentId: !!rowData?.document_id,
      hasWalletAddress: !!walletAddress,
      allConditionsMet: !!(rowData && (rowData?.id || rowData?.document_id))
    });
    
    if (rowData && (rowData?.id || rowData?.document_id)) {
      console.log('✅ Navigating to document view...');
      console.log('📄 rowData.file_entry:', rowData.file_entry);
      console.log('📄 rowData.document_id:', rowData.document_id);
      console.log('📄 rowData.slug:', rowData.slug);
      console.log('📄 rowData.partyAssignment:', rowData.partyAssignment);
      
      // Check which signature provider is configured
      if (isCustom) {
        // Custom Signature System - Use values from Python API
        const documentId = rowData?.document_id;
        const token = rowData?.partyAssignment?.signing_token;

        if (!documentId || !token) {
          console.error('❌ Missing documentId or signing token:', { documentId, token, rowData });
          return;
        }

        const signatureApiUrl = import.meta.env.VITE_SIGNATURE_API_URL || 'http://localhost:5173';
        const fullUrl = `${signatureApiUrl}/?mode=sign&docId=${documentId}&token=${token}`;

        console.log('📄 Opening custom signature in iframe:', fullUrl);
        setSignatureUrl(fullUrl);
        setShowIframe(true);
      } else {
        // DocuSeal (existing behavior)
        console.log('📄 Navigating to DocuSeal:', `/viewDoc/${rowData.file_entry}`);
        navigate('/viewDoc/' + rowData.file_entry, {
          state: {
            slug: rowData?.slug,
            email: rowData?.wallet_email,
            entry: rowData,
          },
        });
      }
    } else {
      console.log('❌ Navigation blocked - missing required data');
    }

    if (
      parsedData &&
      parsedData?.id &&
      isMatchedDoc &&
      isMatchedDoc.length > 0 &&
      slug
    ) {
      if (isCustom) {
        // Custom Signature System - Dynamic values from database
        const documentId = parsedData?.document_id || parsedData?.custom_doc_id;
        const token = isMatchedDoc[0]?.signing_token || isMatchedDoc[0]?.party_token;

        if (!documentId || !token) {
          console.error('❌ Missing documentId or signing token for parsedData:', { documentId, token, parsedData, isMatchedDoc });
          return;
        }

        const signatureApiUrl = import.meta.env.VITE_SIGNATURE_API_URL || 'http://localhost:5173';
        const fullUrl = `${signatureApiUrl}/?mode=sign&docId=${documentId}&token=${token}`;

        setSignatureUrl(fullUrl);
        setShowIframe(true);
      } else {
        navigate('/viewDoc/' + parsedData.id, {
          state: {
            slug: slug,
            email: isMatchedDoc[0]?.email,
            entry: parsedData,
          },
        });
      }
    }
  }, [rowData, walletAddress, isMatchedDoc, isCustom, navigate]);

  const digitalSignature = async (id: number, decrypted: any) => {
    const response = await fetchDigitalSignatureTemplateId(id);
    if (response && response?.slug) {
      setSlug(response.slug);
      if (decrypted?.wallets && decrypted?.wallets.length > 0) {
        const matchingWallets: any = decrypted?.wallets?.filter(
          (wallet: any) =>
            wallet.wallet?.toLowerCase() === walletAddress?.toLowerCase(),
        );
        if (matchingWallets.length > 0) setIsMatchedDoc(matchingWallets);
      }
    }
  };

  return (
    <>
      {showIframe && signatureUrl ? (
        <div className="fixed inset-0 z-50 bg-white">
          <div className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b bg-gray-50 px-16 py-12">
              <h2 className="text-lg font-semibold">Document Signature</h2>
              <button
                onClick={() => {
                  setShowIframe(false);
                  setSignatureUrl('');
                  setRowData({});
                }}
                className="rounded-lg bg-gray-200 px-16 py-8 text-sm font-medium hover:bg-gray-300"
              >
                ← Back to Documents
              </button>
            </div>
            <iframe
              src={signatureUrl}
              className="h-full w-full border-0"
              title="Document Signature"
            />
          </div>
        </div>
      ) : isLoadingDocs ? (
        <div className="bg-gray-100 flex h-screen items-center justify-center px-4">
          <div className="border-gray-300 w-full max-w-sm rounded-2xl border bg-white p-50 text-center shadow-xl">
            <Loader2 className="w-48 h-48 animate-spin text-blue-500 mx-auto mb-16" />
            <p className="text-gray-700 text-xl font-semibold">
              Loading signature documents...
            </p>
            <p className="text-gray-500 text-sm mt-8">
              Party ID: {partyId || 'Not set'}
            </p>
          </div>
        </div>
      ) : signatureDocuments && signatureDocuments.length > 0 ? (
        <>
          {console.log('📊 Rendering DocTable with', signatureDocuments.length, 'documents')}
          <DocTable
            walletAddress={walletAddress}
            email={null}
            allDigitalSignDocs={signatureDocuments}
            setRowData={setRowData}
            partyId={partyId}
          />
        </>
      ) : (
        <div className="bg-gray-100 flex h-screen items-center justify-center px-4">
          <div className="border-gray-300 w-full max-w-sm rounded-2xl border bg-white p-50 text-center shadow-xl">
            <p className="text-gray-700 text-xl font-semibold mb-2">
              No signature documents found
            </p>
            <p className="text-gray-500 text-sm">
              Documents requiring your signature will appear here
            </p>
            <p className="text-gray-400 text-xs mt-8">
              Party ID: {partyId || 'Not set'}
            </p>
          </div>
        </div>
      )}
    </>
  );
}